import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Skeleton, IconButton, Box, Typography } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { MapPin } from "@phosphor-icons/react";
import Landmark from "../cart/address/Landmark";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

const containerStyle = {
  width: "100%",
  height: "410px",
};

const GoogleMapContainer = forwardRef(
  ({ addressDetailsRef, defaultCoordinates, onAddressSelect }, ref) => {
    const [currentPosition, setCurrentPosition] = useState(null);
    const [mapRef, setMapRef] = useState(null);
    const [formattedGoogleAddress, setFormattedGoogleAddress] = useState(null);
    const previousCoordinates = useRef(null);

    // Fallback coordinates if not valid
    const validDefaultCoordinates =
      !isNaN(defaultCoordinates.lat) && !isNaN(defaultCoordinates.lng)
        ? defaultCoordinates
        : {
            lat: 0,
            lng: 0,
          };

    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      libraries: ["places"],
    });

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      searchFromInput: (address) => {
        geocodeAddress(address);
      },
    }));

    // Initialize map position when component mounts or coordinates change
    useEffect(() => {
      if (isLoaded) {
        const coordsChanged = 
          !previousCoordinates.current ||
          previousCoordinates.current.lat !== validDefaultCoordinates.lat ||
          previousCoordinates.current.lng !== validDefaultCoordinates.lng;

        if (coordsChanged) {
          previousCoordinates.current = validDefaultCoordinates;
          
          if (
            validDefaultCoordinates?.lat != 0 &&
            validDefaultCoordinates?.lng != 0
          ) {
            // Set position from saved coordinates (edit mode)
            mapToDefaultLocation(validDefaultCoordinates);
          } else {
            // Get current location (new address mode)
            getCurrentLocation();
          }
        }
      }
    }, [isLoaded, validDefaultCoordinates.lat, validDefaultCoordinates.lng]);

    const mapToDefaultLocation = (coords) => {
      const lat = Number(coords.lat);
      const lng = Number(coords.lng);

      if (!isFinite(lat) || !isFinite(lng)) {
        console.warn("Invalid coordinates:", coords);
        return;
      }

      const coordinates = { lat, lng };

      setCurrentPosition(coordinates);
      addressDetailsRef.current = {
        ...(addressDetailsRef.current || {}),
        coordinates: coordinates,
      };
      getPincodeFromLatLng(coordinates);

      if (mapRef) {
        mapRef.panTo(coordinates);
      }
    };

    const getCurrentLocation = () => {
      if (!isLoaded) return;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentPosition(coords);
          addressDetailsRef.current = {
            ...(addressDetailsRef.current || {}),
            coordinates: coords,
          };
          getPincodeFromLatLng(coords);
          if (mapRef) {
            mapRef.panTo(coords);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          setCurrentPosition(validDefaultCoordinates);
          addressDetailsRef.current = {
            ...(addressDetailsRef.current || {}),
            coordinates: validDefaultCoordinates,
          };
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    };

    const handleMapClick = (event) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setCurrentPosition(newPosition);
      addressDetailsRef.current = {
        ...(addressDetailsRef.current || {}),
        coordinates: newPosition,
      };
      getPincodeFromLatLng(newPosition);
    };

    const handleMarkerDrag = (event) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setCurrentPosition(newPosition);
      addressDetailsRef.current = {
        ...(addressDetailsRef.current || {}),
        coordinates: newPosition,
      };
      getPincodeFromLatLng(newPosition);
    };

    // Geocode address from text input (called from parent)
    const geocodeAddress = (address) => {
      if (!address || !window.google || address.length < 3) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          const newPosition = { lat, lng };
          setCurrentPosition(newPosition);

          if (mapRef) {
            mapRef.panTo(newPosition);
            mapRef.setZoom(16);
          }

          addressDetailsRef.current = {
            ...(addressDetailsRef.current || {}),
            coordinates: newPosition,
          };

          getPincodeFromLatLng(newPosition);
        }
      });
    };

    const getPincodeFromLatLng = (location) => {
      if (!window.google || !window.google.maps) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location }, (results, status) => {
        if (status === "OK" && results[0]) {
          let parsedAddress = {
            area: "",
            state: "",
            pincode: "",
            district: "",
            googleMapAddress: "",
          };

          let bestResult = results.find((result) =>
            result.address_components.some((component) =>
              component.types.includes("postal_code"),
            ),
          );

          if (!bestResult) {
            bestResult = results[0];
          }

          parsedAddress.googleMapAddress = bestResult.formatted_address;
          setFormattedGoogleAddress(bestResult.formatted_address);

          for (const component of bestResult.address_components) {
            if (component.types.includes("postal_code")) {
              parsedAddress.pincode = component.long_name;
            } else if (
              component.types.includes("administrative_area_level_1")
            ) {
              parsedAddress.state = component.long_name;
            } else if (
              component.types.includes("administrative_area_level_3")
            ) {
              parsedAddress.district = component.long_name;
            } else if (component.types.includes("locality")) {
              parsedAddress.area = component.long_name;
            }
          }

          addressDetailsRef.current = {
            ...(addressDetailsRef.current || {}),
            ...parsedAddress,
          };

          // IMPORTANT: Always call onAddressSelect after updating addressDetailsRef
          // This ensures the parent form gets updated with the new address
          if (onAddressSelect) {
            onAddressSelect(bestResult.formatted_address);
          }
        } else {
          console.error("Geocoder failed due to:", status);
          setFormattedGoogleAddress(null);
        }
      });
    };

    if (!isLoaded) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={420}
          sx={{ borderRadius: 1 }}
        />
      );
    }

    return (
      <Box sx={{ position: "relative" }}>
        <Landmark
          mapToDefaultLocation={mapToDefaultLocation}
          onAddressSelect={onAddressSelect}
        />

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={currentPosition}
          zoom={16}
          onClick={handleMapClick}
          onLoad={(map) => setMapRef(map)}
          options={{
            fullscreenControl: true,
            disableDefaultUI: true,
          }}
        >
          {currentPosition && (
            <Marker
              position={currentPosition}
              draggable
              onDragEnd={handleMarkerDrag}
            />
          )}
        </GoogleMap>

        <Box
          sx={{
            py: 1,
            px: 2,
            bgcolor: "var(--neutral-200)",
            borderRadius: "8px",
            mt: 2,
            minHeight: "50px",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--neutral-black)",
            }}
          >
            <MapPin
              size={14}
              color="red"
              style={{ marginRight: "8px" }}
              weight="fill"
            />
            {formattedGoogleAddress}
          </Typography>
        </Box>

        <IconButton
          onClick={getCurrentLocation}
          sx={{
            position: "absolute",
            bottom: 100,
            right: 16,
            backgroundColor: "#fff",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#eee",
            },
          }}
        >
          <MyLocationIcon />
        </IconButton>
      </Box>
    );
  },
);

export default GoogleMapContainer;